import {debounce} from './utils';

export class Gallery {
	MAX_SCALE = 2;

	constructor(node) {
		this.node = node;

		const items = Array.from(this.node.querySelectorAll(':scope > .gallery-item'));
		const thumbs = Array.from(this.node.querySelectorAll('.gallery-thumb'));
		this.elements = items.map((item, index) => new GalleryElement(thumbs[index], item, this, index));

		this.activeElement = this.elements[0];

		this.zoom = new GalleryZoom(this);
		this.lightbox = new GalleryLightBox(this);
	}

	setActiveElement(element) {
		if (element === this.activeElement) return;
		this.activeElement.setActive(true);
		element.setActive();
		this.activeElement = element;
	}
}

class GalleryElement {
	constructor(thumbNode, itemNode, gallery, index) {
		this.thumb = new GalleryThumb(thumbNode, this);
		this.item = new GalleryItem(itemNode, this);
		this.gallery = gallery;
		this.index = index;
	}

	setActive(invert = false) {
		this.item.setActive(invert)
		this.thumb.setActive(invert)
	}
}

class GalleryItem {
	constructor(node, element) {
		this.node = node;
		this.element = element;

		if (this.zoomisEnabled) {
			if (!this.imgWidth || !this.imgHeight) this._loadNaturalSizes();

			this.area = new GalleryArea(this);

			this.node.addEventListener('mouseenter', e => {
				// console.debug('mouseenter item', e, this);
				this.element.gallery.zoom.load(this.element);
				this.area.load(this);
			})

			this.node.addEventListener('mousemove', e => {
				// console.debug('movemove item', e, this);
				const scale = this.maxScale;

				// position of the cursor relative to the image [px]
				const rect = this.imgNode.getBoundingClientRect();
				const relCursorX = e.clientX - rect.left;
				const relCursorY = e.clientY - rect.top;
				// console.debug('relCursorX: %s | relCursorY: %s', relCursorX, relCursorY)

				// relative position of the cursor on the image [float-intervall [0, 1]]
				const relL = Math.min(1, Math.max(0, relCursorX / rect.width));
				const relT = Math.min(1, Math.max(0, relCursorY / rect.height));
				// console.debug('relL: %s | relT: %s', relL, relT)

				// left-top position of the area,
				// where the cursor is the center, bounded by the borders
				const halfArea = 1 / (scale * 2);
				const left = (Math.min(Math.max(relL, halfArea), 1 - halfArea) - halfArea);
				const top = (Math.min(Math.max(relT, halfArea), 1 - halfArea) - halfArea);
				// console.debug('left: %s | top: %s', left, top)

				this.element.gallery.zoom.move(left, top, this.element);
				this.area.move(left, top);
			});

			this.node.addEventListener('mouseleave', e => {
				// console.debug('mouseleave item', e, this);

				this.element.gallery.zoom.unload();
				this.element.item.area.unload();
			});
		}


		if (this.lightboxisEnabled) {
			this.node.addEventListener('click', e => {
				this.element.gallery.zoom.unload();
				this.element.item.area.unload();
				this.element.gallery.lightbox.load(this);
			})
		}
	}

	setActive(invert = false) {
		this.node.classList.toggle('is-active', !invert);
	}

	get zoomisEnabled() {
		// only for (hopefully) high resolution images.
		return this.imgNode !== null;
	}

	get lightboxisEnabled() {
		return this.imgNode !== null || this.youtubeNode !== null;
		return true; //FIXME
		// only for (hopefully) high resolution images.
		return this.node.querySelector('.gallery-image') !== null;
	}

	get imgNode() {
		return this.node.querySelector('.gallery-image');
	}

	get youtubeNode() {
		return this.node.querySelector('.gallery-youtube');
	}

	get imgHeight() {
		return parseInt(this.imgNode.dataset.originalSrcHeight);
	}

	set imgHeight(value) {
		this.imgNode.dataset.originalSrcHeight = value.toString();
	}

	get imgWidth() {
		return parseInt(this.imgNode.dataset.originalSrcWidth);
	}

	set imgWidth(value) {
		this.imgNode.dataset.originalSrcWidth = value.toString();
	}

	get imgSrc() {
		if (this.imgNode.dataset.zoom_url) {
			return this.imgNode.dataset.zoom_url;
		}
		const baseUrl = this.imgNode.src.split('=').slice(0, -1).join('=');
		return `${baseUrl}=s1600`;
	}

	/**
	 * Create a tmp image node, read the natural sizes and store them
	 * @private
	 */
	_loadNaturalSizes() {
		const tmpNode = document.createElement('img');
		tmpNode.onload = () => {
			// console.debug('loaded', tmpNode, tmpNode.naturalHeight, tmpNode.naturalWidth);
			this.imgHeight = tmpNode.naturalHeight;
			this.imgWidth = tmpNode.naturalWidth;
			tmpNode.remove();
		}
		tmpNode.src = this.imgSrc;
		tmpNode.style.visibility = 'hidden';
		document.body.append(tmpNode)
		// console.debug(tmpNode.naturalHeight, tmpNode.naturalWidth);
	}

	/**
	 * Get the maximal possible scale of an image.
	 * Consider the size of the zoom area (square) and a MAX_SCALE value.
	 * Scale is always in the intervall [1, MAX_SCALE]
	 * @return {number} The scale factor
	 */
	get maxScale() {
		const maxBorders = this.element.gallery.zoom.maxBorders;
		return Math.min(Math.max(this.imgHeight / maxBorders.height, this.imgWidth / maxBorders.width, 1), this.element.gallery.MAX_SCALE);
	}

	get orientation() {
		return this.imgHeight > this.imgWidth ? 'portrait' : 'landscape';
	}
}

class GalleryThumb {
	constructor(node, element) {
		this.node = node;
		this.element = element;

		this.node.addEventListener('mouseenter', e => {
			// console.debug('mouseenter thumb', e, this);
			this.element.gallery.setActiveElement(this.element);
		});

		this.node.addEventListener('click', e => {
			// console.debug('click thumb', e, this);
			if (this.element.item.lightboxisEnabled) this.element.gallery.lightbox.load(this.element.item);
		});
	}

	setActive(invert = false) {
		this.node.classList.toggle('is-active', !invert);
	}
}

class GalleryArea {
	constructor(item) {
		this.item = item;

		this.node = document.createElement('div');
		this.node.className = 'gallery-zoom-area';
		this.item.node.appendChild(this.node);
	}

	load() {
		const scale = this.item.maxScale;
		const rect = this.item.imgNode.getBoundingClientRect();
		this.node.style.display = 'block';
		this.node.style.width = `${rect.width / scale}px`;
		this.node.style.height = `${rect.height / scale}px`;
		// this.node.style.left = `0px`;
		// this.node.style.top = `0px`;
	}

	move(left, top) {
		this.node.style.left = `${left * this.item.imgNode.offsetWidth}px`;
		this.node.style.top = `${top * this.item.imgNode.offsetHeight}px`;
	}

	unload() {
		this.node.style.display = '';
	}
}


class GalleryZoom {
	constructor(gallery) {
		this.gallery = gallery;
		this.element = null;

		this.node = document.createElement('div');
		this.node.classList.add('gallery-zoom');
		document.body.append(this.node);
		this.imgNode = document.createElement('img');
		this.node.append(this.imgNode);
	}

	get maxBorders() {
		const galleryRect = this.gallery.node.getBoundingClientRect();
		const SPACE = Math.min(window.innerHeight, window.innerWidth) * .08;
		const style = getComputedStyle(this.node);
		const rect = {
			left: galleryRect.right + SPACE - parseInt(style.borderLeft),
			top: SPACE - parseInt(style.borderTopWidth),
			right: SPACE - parseInt(style.borderRightWidth),
			bottom: SPACE - parseInt(style.borderBottomWidth),
		}
		rect.width = window.innerWidth - rect.left - rect.right - parseInt(style.borderLeft) - parseInt(style.borderRightWidth);
		rect.height = window.innerHeight - rect.top - rect.bottom - parseInt(style.borderTopWidth) - parseInt(style.borderBottomWidth);
		return rect;
	}

	load(element) {
		this.element = element;
		this.node.style.display = 'block';
		this.node.style.left = `${this.maxBorders.left}px`;
		this.node.style.top = `${this.maxBorders.top}px`;

		this.imgNode.src = element.item.imgSrc;
		// this.imgNode.style.top = '0px';
		// this.imgNode.style.left = '0px';

		const scale = element.item.maxScale;

		let nodeWidth, nodeHeight, imgHeight, imgWidth;

		// choose the smaller (possible) one, because we're square
		nodeHeight = nodeWidth = Math.min(this.maxBorders.height, this.maxBorders.width);
		this.node.style.width = `${nodeWidth}px`;
		this.node.style.height = `${nodeHeight}px`;

		imgHeight = nodeHeight * scale;
		imgWidth = nodeWidth * scale;
		this.imgNode.style.width = `${imgWidth}px`;
		this.imgNode.style.height = `${imgHeight}px`;
	}

	move(left, top, element) {
		this.imgNode.style.left = `${-this.imgNode.offsetWidth * left}px`;
		this.imgNode.style.top = `${-this.imgNode.offsetHeight * top}px`;
	}

	unload() {
		this.node.style.display = '';
	}
}

class GalleryLightBox {
	constructor(gallery) {
		this.gallery = gallery;
		this.item = null;

		this.bindedOnKeyboard = this.onKeyboard.bind(this);
		this.bindedOnWheel = debounce(this.onWheel.bind(this), 150, true);

		this.items = this.gallery.elements.filter(elem => elem.item.lightboxisEnabled);

		this.node = document.createElement('div');
		this.node.classList.add('gallery-lightbox');
		this.node.style.visibility = 'hidden';
		document.body.append(this.node);

		this.contentNode = document.createElement('div');
		this.contentNode.className = 'gallery-lightbox-content';
		this.node.append(this.contentNode);

		this.leftNode = document.createElement('div');
		this.leftNode.className = 'gallery-lightbox-control-area gallery-lightbox-control-area--left';
		this.leftNode.innerHTML = '&#10094;';
		this.node.append(this.leftNode);

		this.rightNode = document.createElement('div');
		this.rightNode.className = 'gallery-lightbox-control-area gallery-lightbox-control-area--right';
		this.rightNode.innerHTML = '&#10095;';
		this.node.append(this.rightNode);

		this.closeNode = document.createElement('div');
		this.closeNode.className = 'gallery-lightbox-close';
		this.closeNode.innerHTML = '&times;';
		this.node.append(this.closeNode);

		this.infoNode = document.createElement('div');
		this.infoNode.className = 'gallery-lightbox-info';
		this.node.append(this.infoNode);

		this.node.addEventListener('click', e => {
			// console.debug('click lightbox', e, this);
			if (e.currentTarget === e.target) // clicked on the "background"
				this.unload();
		});

		this.leftNode.addEventListener('click', this.showPrev.bind(this));
		this.rightNode.addEventListener('click', this.showNext.bind(this));

		this.closeNode.addEventListener('click', e => {
			this.unload();
		});
	}

	showPrev() {
		let prevIndex = this.items.indexOf(this.item.element) - 1;
		if (prevIndex === -1) prevIndex = this.items.length - 1;
		this.change(this.items[prevIndex].item);
	}

	showNext() {
		let nextIndex = this.items.indexOf(this.item.element) + 1;
		if (nextIndex === this.items.length) nextIndex = 0;
		this.change(this.items[nextIndex].item);
	}

	load(item) {
		document.documentElement.classList.add('no-scroll');
		document.addEventListener('keydown', this.bindedOnKeyboard);
		document.addEventListener('wheel', this.bindedOnWheel);
		this.node.style.visibility = 'visible';
		this.change(item);
	}

	change(item) {
		// console.debug('Change %o', item);
		this.contentNode.innerHTML = '';
		[...item.node.children].forEach(child => {
			if (child.classList.contains('gallery-zoom-area')) return;

			const newNode = child.cloneNode(true);
			if (newNode.classList.contains('gallery-image')) {
				// FIXME: This is very hacky, we need a better structure!
				newNode.src = item.imgSrc;
			}
			this.contentNode.append(newNode);
		})
		this.item = item;
		this.infoNode.innerHTML = `${this.items.indexOf(item.element) + 1} / ${this.items.length}`;
	}

	unload() {
		document.documentElement.classList.remove('no-scroll');
		document.removeEventListener('keydown', this.bindedOnKeyboard);
		document.removeEventListener('wheel', this.bindedOnWheel);
		this.node.style.visibility = 'hidden';
		this.contentNode.innerHTML = '';
	}

	onKeyboard(e) {
		if (e.code === 'ArrowUp' || e.code === 'ArrowLeft' || e.code === 'PageUp') {
			this.showPrev();
		} else if (e.code === 'ArrowDown' || e.code === 'ArrowRight' || e.code === 'PageDown') {
			this.showNext();
		} else if (e.code === 'Escape') {
			this.unload();
		}
	}

	onWheel(e) {
		// console.debug('Caught wheeel event', e, this);
		if (e.deltaY < 0 || e.deltaX < 0) {
			this.showPrev();
		} else if (e.deltaY > 0 || e.deltaX > 0) {
			this.showNext();
		}
	}
}


export class Carousel {

	constructor(node) {
		this.node = node;
		this.itemsNode = this.node.querySelector('.schulte-carousel-stage');
		if (this.itemsNode === null)
			throw new TypeError(`Didn't found the stage element of schulte-carousel.`);
		this.originalItems = [...this.itemsNode.children];
		this.clonedItems = [];
		this.items = this.originalItems.slice();
		this.itemsAmount = this.originalItems.length;
		this.firstActive = 0;

		this.init();
		window.addEventListener('resize', this.reload.bind(this));
	}

	init(reload = false) {
		this.display = this._chooseDisplay();

		if (this.itemsAmount <= this.display) {
			console.debug('Not enough items for a carousel');
			return;
		}

		// Clone the first display - 1 items and append them to the end,
		// to close the circle between the last and first one
		this.clonedItems = this.originalItems.slice(0, this.display - 1)
			.map(item => item.cloneNode(true));
		this.clonedItems.forEach(item => this.itemsNode.append(item));

		this.items = [...this.itemsNode.children];

		// Hide all, except the first chunk
		this.items.slice(this.display).forEach(item => item.classList.add('is-hidden'));
		// Show the first chunk
		this.items.slice(0, this.display).forEach(item => item.classList.remove('is-hidden'));

		// Listen for clicks on the controls
		this.node.querySelectorAll('.schulte-carousel-control').forEach(control => {
			control.classList.remove('is-hidden');
			this.onControlClick = this.onControlClick || (e => {
				e.preventDefault();
				let next = this.firstActive + (control.dataset.direction === 'left' ? -1 : 1);
				if (next === -1) next = this.itemsAmount - 1;
				if (next === this.itemsAmount) next = 0;
				this.slide(next);
				// if the user clicked to fast the browser interprets this as double-click and selects a box.
				window.getSelection().removeAllRanges();
			});
			control.addEventListener('click', this.onControlClick)
		})

	}

	_chooseDisplay() {
		if (window.matchMedia(`(min-width: 80em)`).matches) {
			return 4;
		} else if (window.matchMedia(`(min-width: 54em)`).matches) {
			return 3;
		} else if (window.matchMedia(`(min-width: 32em)`).matches) {
			return 2;
		} else {
			return 1;
		}
	}

	reload() {
		// reset carousel (remove clones and hide controls)
		this.clonedItems.forEach(item => item.remove());
		this.node.querySelectorAll('.schulte-carousel-control').forEach(control => {
			control.classList.add('is-hidden');
			control.removeEventListener('click', this.onControlClick);
		});
		this.init(true);
	}

	slide(to) {
		// console.debug('Slide to %s', to);
		this.firstActive = to;
		this.items.forEach(item => item.classList.add('is-hidden'));
		this.items.slice(this.firstActive, this.firstActive + this.display)
			.forEach(item => item.classList.remove('is-hidden'));
	}
}

export default class Elements {

	constructor(app) {
		this.app = app;
		const self = this;

		this.carousels = [];
		self.initGallery();
		self.initCarousel();
	}

	initGallery() {
		const self = this;

		this.galleries = [...document.querySelectorAll('.variant-gallery')].map(node => new Gallery(node));
	}

	initCarousel() {
		document.querySelectorAll('.schulte-carousel')
			.forEach(node => {
				if (this.carousels.find(c => c.node === node)) {
					console.warn('%o has already an %s instance', node, Carousel.prototype.toString());
				}
				try {
					this.carousels.push(new Carousel(node));
				} catch (e) {
					console.error('Failed to init carousel %o', node);
					console.error(e);
				}
			});
	}
}
